import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE,
} from '@/shared/config';
import { getSharedDCCTVolumeInfo } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { InlineFormError } from '@/shared/ui';

import { reportsModel } from '@/entities/reports';

import { SelectedUploadedMedicalImages } from '@/features/medicalImage';

import {
  useFullHeadMprSliceGroups,
  useFullHeadMprSliceSelection,
  useFullHeadMprSliceTileSrcs,
  useToothBasisSeries,
  useToothBasisSeriesPrefetch,
  useToothFullHeadMprSliceRecords,
  useToothSliceCardVisibility,
  useToothSliceOptions,
  useToothSliceSource,
} from '../../hooks';
import {
  isFullHeadMprSlicesUnavailable,
  resolveFullHeadMprSlicesBranch,
  resolveToothSliceThicknessOptionsMm,
} from '../../lib';
import { FullHeadMprSliceImage } from '../FullHeadMprSliceImage';
import { FullHeadMprSlicesLoadingPlaceholder } from '../FullHeadMprSlicesLoadingPlaceholder';
import { FullHeadMprSliceZoomModal } from '../FullHeadMprSliceZoomModal';

import styles from './GpToothCardFullHeadMprSlices.module.scss';

type GpToothCardFullHeadMprSlicesProps = {
  isVolumeReady: boolean;
  toothID: string;
};

const GpToothCardFullHeadMprTiles: FC<GpToothCardFullHeadMprSlicesProps> = (
  props,
) => {
  const { isVolumeReady, toothID } = props;

  const { isMobile } = useMedia();

  const { setTargetNode } = useToothSliceCardVisibility(toothID);

  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );
  const isActiveTooth = toothID === activeToothID;
  const isLoadEnabled = isVolumeReady;

  const input = useToothSliceSource(toothID);
  const toothInput = input?.kind === 'tooth' ? input : null;

  useToothBasisSeriesPrefetch({
    input: toothInput,
    isEnabled: isVolumeReady && isActiveTooth,
  });

  const thicknessOptionsMm = resolveToothSliceThicknessOptionsMm(
    toothInput ? getSharedDCCTVolumeInfo(toothInput.volumeFileID) : null,
  );

  const { sliceOptions } = useToothSliceOptions({
    thicknessOptionsMm,
    toothID,
  });

  const recordsByTileID = useToothFullHeadMprSliceRecords(toothID);

  const {
    frames,
    loading,
    massCentroidMm,
    pendingSeries,
    profiles,
    series,
    slabThicknessMm,
    sliceStepMm,
  } = useToothBasisSeries({
    input: toothInput,
    isEnabled: isLoadEnabled,
    isPanelBlocking: true,
    sliceOptions,
  });

  const seriesGroups = useFullHeadMprSliceGroups({
    frames,
    pendingSeries,
    series,
    slabThicknessMm,
    sliceOptions,
    sliceStepMm,
    toothID,
  });

  const {
    fullHeadMprSliceGroups,
    hasSavedViews,
    savedViewsLoading,
    resolveViewSelection,
    selectedTiles,
    setZoomedTileID,
    zoomedTile,
  } = useFullHeadMprSliceSelection({
    fullHeadMprSliceGroups: seriesGroups,
    input: toothInput,
    massCentroidMm,
    profiles,
    recordsByTileID,
    sliceOptions,
    toothID,
    isEnabled: isLoadEnabled,
    isSelectionReady: !toothInput || loading === 'succeeded',
  });

  const { resolveTileSrc } = useFullHeadMprSliceTileSrcs({
    recordsByTileID,
    tiles: selectedTiles,
  });

  const sliceHeight = isMobile
    ? DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE
    : DEFAULT_MEDICAL_IMAGE_HEIGHT;

  const branch = resolveFullHeadMprSlicesBranch({
    groupCount: fullHeadMprSliceGroups.length + (hasSavedViews ? 1 : 0),
    hasSavedViews,
    hasSeries: !!series,
    hasToothInput: !!toothInput,
    isEnabled: true,
    isUnavailable:
      !hasSavedViews &&
      isFullHeadMprSlicesUnavailable({ input, isVolumeReady }),
    loading,
    savedViewsLoading,
  });

  const zoomedGroup =
    fullHeadMprSliceGroups.find(
      (group) => group.seriesName === zoomedTile?.seriesName,
    ) ?? null;

  return (
    <>
      {branch === 'failed' && (
        <InlineFormError
          errorMessage={
            <FormattedMessage
              id="global.defaultError"
              defaultMessage="Something went wrong. Try again later or contact our support team."
            />
          }
        />
      )}
      {branch === 'skeleton' && (
        <FullHeadMprSlicesLoadingPlaceholder
          height={sliceHeight}
          setNodeRef={setTargetNode}
        />
      )}
      {branch === 'tiles' &&
        selectedTiles.map((tile) => {
          const group = fullHeadMprSliceGroups.find(
            (clientGroup) => clientGroup.seriesName === tile.seriesName,
          );

          return group || tile.kind === 'view' ? (
            <div key={tile.id} className={styles.sliceWrapper}>
              <FullHeadMprSliceImage
                group={group}
                src={resolveTileSrc(tile)}
                tile={tile}
                record={recordsByTileID[tile.id]}
                onImageClick={() => setZoomedTileID(tile.id)}
              />
            </div>
          ) : null;
        })}

      <FullHeadMprSliceZoomModal
        group={zoomedGroup}
        groups={fullHeadMprSliceGroups}
        input={toothInput}
        tile={zoomedTile}
        onClose={() => setZoomedTileID('')}
        resolveViewSelection={resolveViewSelection}
      />
    </>
  );
};

export const GpToothCardFullHeadMprSlices: FC<
  GpToothCardFullHeadMprSlicesProps
> = (props) => (
  <>
    <GpToothCardFullHeadMprTiles {...props} />
    <SelectedUploadedMedicalImages toothID={props.toothID} showOrder={false} />
  </>
);
